import type { Attachment } from "@catmail/shared";

/**
 * 본문에 박힌 이미지(`cid:`)를 화면에 띄울 수 있는 모양으로 바꾼다.
 *
 * HTML 메일에서 로고와 사진은 대개 원격 URL이 아니라 **같은 메일에 첨부된 파일**
 * 이고, 본문은 그것을 `<img src="cid:logo@example">`로 가리킨다. 우리는 원문을
 * 스크립트 없는 sandbox iframe에 넣고 그 안에 `default-src 'none'` CSP를 박기
 * 때문에, `cid:`도 우리 서버 주소도 그대로는 아무것도 불러오지 못한다 —
 * 지금까지 박힌 이미지가 전부 빈 칸으로 나오던 이유다.
 *
 * 그렇다고 CSP에 우리 출처를 열어줄 수도 없다. sandbox iframe은 출처가 없는
 * 상태라 요청에 세션 쿠키가 붙지 않아 어차피 401이 돌아온다.
 *
 * 그래서 **바깥(이 창)에서 쿠키를 달고 받아온 뒤 `data:`로 바꿔 넣는다.**
 * `img-src data:`는 이미 열려 있고, data는 네트워크로 나가지 않으므로 추적
 * 픽셀을 막아둔 원칙도 그대로다.
 */

/** 이미지 하나의 상한. 이보다 크면 본문에 박힌 장식이 아니라 첨부로 본다. */
const MAX_IMAGE_BYTES = 4 * 1024 * 1024;

/** 한 통에서 인라인으로 펼칠 총량. srcDoc 문자열이 무한정 커지지 않게 막는다. */
const MAX_TOTAL_BYTES = 8 * 1024 * 1024;

/** cid로 찾을 수 있게 만든 표. 키는 소문자 Content-ID, 값은 data: URI. */
export type InlineImageMap = Record<string, string>;

function toBase64(buffer: ArrayBuffer): string {
  const bytes = new Uint8Array(buffer);
  // 한 번에 다 넘기면 인자가 너무 많아 스택이 터진다. 조각내서 잇는다.
  const CHUNK = 0x8000;
  let binary = "";
  for (let offset = 0; offset < bytes.length; offset += CHUNK) {
    binary += String.fromCharCode(...bytes.subarray(offset, offset + CHUNK));
  }
  return btoa(binary);
}

/** 이 첨부가 본문에 박힐 수 있는 것인가. */
function isEmbeddable(attachment: Attachment): boolean {
  return (
    attachment.inline &&
    attachment.contentId !== null &&
    attachment.downloadUrl.length > 0 &&
    attachment.mimeType.startsWith("image/") &&
    attachment.size <= MAX_IMAGE_BYTES
  );
}

export function hasInlineImages(attachments: readonly Attachment[]): boolean {
  return attachments.some(isEmbeddable);
}

export async function loadInlineImages(
  attachments: readonly Attachment[],
): Promise<InlineImageMap> {
  const map: InlineImageMap = {};
  let total = 0;

  for (const attachment of attachments) {
    if (!isEmbeddable(attachment) || attachment.contentId === null) continue;
    if (total + attachment.size > MAX_TOTAL_BYTES) break;

    try {
      const response = await fetch(attachment.downloadUrl, {
        credentials: "same-origin",
      });
      if (!response.ok) continue;

      const buffer = await response.arrayBuffer();
      total += buffer.byteLength;

      // 서버는 브라우저가 첨부를 실행하지 못하게 항상 octet-stream으로 준다.
      // 화면에 그리려면 원래 형식이 필요하므로 DB에 적힌 값을 쓴다.
      map[attachment.contentId.toLowerCase()] =
        `data:${attachment.mimeType};base64,${toBase64(buffer)}`;
    } catch {
      // 한 장 못 받았다고 본문 전체를 접을 이유는 없다. 그 자리만 빈다.
    }
  }

  return map;
}
